import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Truck, Package, Phone, MapPin, Clock, CheckCircle2,
  ArrowRight, LogOut, User, Star, ChevronRight, MessageSquare, Navigation, Shield, Languages
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = ['assigned', 'picked_up', 'on_the_way', 'delivered'];

export default function DriverPortalPage() {
  const { t } = useTranslation();
  const { lang, dir, toggleLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const branchParam = searchParams.get('branch') || '';
  const [driver, setDriver] = useState(null);
  const [phone, setPhone] = useState('');
  const [branchId, setBranchId] = useState(branchParam);
  const [deliveries, setDeliveries] = useState({ active: [], completed: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  const fetchDeliveries = useCallback(async () => {
    if (!driver) return;
    try {
      const res = await fetch(`${API}/api/driver-portal/deliveries/${driver.id}`);
      const data = await res.json();
      setDeliveries(data);
    } catch (e) { console.error(e); }
  }, [driver]);

  useEffect(() => { if (driver) fetchDeliveries(); }, [driver, fetchDeliveries]);

  // WebSocket for real-time delivery notifications
  useEffect(() => {
    if (!driver) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/driver/${driver.id}`);
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.event === 'new_delivery') {
        // Play notification sound
        try { audioRef.current?.play(); } catch {}
        fetchDeliveries();
      }
    };
    return () => { clearInterval(ping); ws.close(); };
  }, [driver, fetchDeliveries]);

  const login = async (overridePhone, overrideBranch) => {
    const loginPhone = overridePhone || phone;
    const loginBranch = overrideBranch || branchId;
    if (!loginPhone || !loginBranch) { setError(t('driverPortal.fillFields')); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/driver-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: loginPhone, branch_id: loginBranch })
      });
      if (!res.ok) { setError(t('driverPortal.driverNotFound')); setLoading(false); return; }
      const data = await res.json();
      setDriver(data);
      localStorage.setItem('driver_session', JSON.stringify(data));
    } catch { setError(t('driverPortal.connectionError')); }
    setLoading(false);
  };

  // Quick demo login - fetches branch from demo API
  const demoLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const demoRes = await fetch(`${API}/api/auth/demo`, { method: 'POST' });
      const demoData = await demoRes.json();
      const branchRes = await fetch(`${API}/api/branches`, { headers: { Authorization: `Bearer ${demoData.token}` } });
      const branches = await branchRes.json();
      const demoBranch = branches[0]?.id || '';
      if (!demoBranch) { setError(t('driverPortal.noDemoBranch')); setLoading(false); return; }
      setBranchId(demoBranch);
      setPhone('0551002002');
      await login('0551002002', demoBranch);
    } catch { setError(t('driverPortal.connectionError')); }
    setLoading(false);
  };

  const updateDeliveryStatus = async (deliveryId, newStatus) => {
    try {
      await fetch(`${API}/api/driver-portal/deliveries/${deliveryId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchDeliveries();
    } catch (e) { console.error(e); }
  };

  const logout = () => {
    setDriver(null);
    localStorage.removeItem('driver_session');
  };

  // Auto-login from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('driver_session');
    if (saved) {
      try { setDriver(JSON.parse(saved)); } catch {}
    }
  }, []);

  const getNextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    return idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  const getNextStatusLabel = (current) => {
    const next = getNextStatus(current);
    if (!next) return null;
    const labels = {
      picked_up: t('driverPortal.pickedUp'),
      on_the_way: t('driverPortal.onTheWay'),
      delivered: t('driverPortal.delivered'),
    };
    return labels[next];
  };

  const getStatusColor = (status) => {
    const colors = { assigned: 'bg-blue-500', picked_up: 'bg-amber-500', on_the_way: 'bg-orange-500', delivered: 'bg-emerald-500' };
    return colors[status] || 'bg-zinc-500';
  };

  // Format phone for WhatsApp (remove + and leading zeros)
  const formatWAPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/[^0-9]/g, '');
  };

  // Open WhatsApp with pre-written message
  const openWhatsApp = (phone, message) => {
    const waPhone = formatWAPhone(phone);
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/${waPhone}?text=${encoded}`, '_blank');
  };

  // Open Google Maps with address
  const openMaps = (address) => {
    if (!address) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  // Get restaurant name from driver's branch
  const restaurantName = 'مطعم الأصيل';

  // WhatsApp message builder using i18n
  const getWAMessage = (status, customerName) => {
    const key = {
      assigned: 'driverPortal.waAssigned',
      picked_up: 'driverPortal.waPickedUp',
      on_the_way: 'driverPortal.waOnTheWay',
      arrived: 'driverPortal.waArrived',
    }[status];
    return t(key, { name: customerName, restaurant: restaurantName });
  };

  // Status label from i18n
  const getStatusLabel = (status) => {
    const keys = { assigned: 'fleet.statusAssigned', picked_up: 'fleet.statusPickedUp', on_the_way: 'fleet.statusOnTheWay', delivered: 'fleet.statusDelivered', cancelled: 'fleet.statusCancelled' };
    return t(keys[status] || status);
  };

  // Login Screen
  if (!driver) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" dir={dir} data-testid="driver-login-page">
        <audio ref={audioRef} src="data:audio/wav;base64,UklGRl9vT19teleVBFbXQAAAA=" preload="auto" />
        {/* Language toggle */}
        <button onClick={toggleLanguage} className="fixed top-4 left-4 text-xs text-zinc-400 hover:text-white bg-zinc-800 px-3 py-1.5 rounded-full transition-colors z-50" data-testid="driver-lang-toggle">
          {lang === 'ar' ? 'EN' : 'عربي'}
        </button>
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
              <Truck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              {t('driverPortal.title')}
            </h1>
            <p className="text-sm text-zinc-400 mt-1">Zouq AI Fleet</p>
          </div>

          {/* Quick Demo Login */}
          <button onClick={demoLogin} disabled={loading}
            className="w-full mb-4 py-3 rounded-xl bg-gradient-to-l from-amber-600 to-orange-600 text-white font-bold text-sm hover:from-amber-500 hover:to-orange-500 transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50"
            data-testid="demo-login-btn">
            {loading ? '...' : t('driverPortal.quickDemo')}
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[10px] text-zinc-600">{t('driverPortal.orLoginWith')}</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">{t('driverPortal.phone')}</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder={t('driverPortal.phonePlaceholder')} className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="driver-login-phone" />
              <p className="text-[10px] text-zinc-600 mt-1">{t('driverPortal.phoneHint')}</p>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">{t('driverPortal.branchId')}</label>
              <Input value={branchId} onChange={e => setBranchId(e.target.value)}
                placeholder={t('driverPortal.branchPlaceholder')} className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="driver-login-branch" />
            </div>
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <Button onClick={() => login()} disabled={loading} className="w-full bg-zinc-700 hover:bg-zinc-600 text-white h-11" data-testid="driver-login-btn">
              {loading ? '...' : t('driverPortal.login')}
            </Button>
          </div>

          {/* Demo hint */}
          <div className="mt-4 bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-3" data-testid="demo-hint">
            <p className="text-[10px] text-zinc-500 text-center mb-1.5">{t('driverPortal.demoPhones')}</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {['0551001001', '0551002002', '0551003003'].map(p => (
                <button key={p} onClick={() => setPhone(p)}
                  className="text-[10px] px-2 py-1 rounded-md bg-zinc-800 text-amber-400/70 hover:text-amber-400 hover:bg-zinc-700 transition-colors font-mono"
                  data-testid={`hint-phone-${p}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Driver Dashboard
  return (
    <div className="min-h-screen bg-zinc-950 text-white" dir={dir} data-testid="driver-portal">
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRl9vT19teleVBFbXQAAAA=" preload="auto" />

      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>{driver.name}</p>
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-400">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>{t('driverPortal.connected')}</span>
              <span>-</span>
              <Star className="w-3 h-3 text-amber-400" />
              <span>{driver.rating}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleLanguage} className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded transition-colors" data-testid="driver-lang-toggle-dash">
            {lang === 'ar' ? 'EN' : 'عربي'}
          </button>
          <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white" data-testid="driver-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 p-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-amber-400">{deliveries.active?.length || 0}</p>
          <p className="text-[10px] text-zinc-500">{t('driverPortal.active')}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-emerald-400">{deliveries.completed?.length || 0}</p>
          <p className="text-[10px] text-zinc-500">{t('driverPortal.completed')}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-white">{driver.total_deliveries || 0}</p>
          <p className="text-[10px] text-zinc-500">{t('driverPortal.total')}</p>
        </div>
      </div>

      {/* Active Deliveries */}
      <div className="px-3 mt-2">
        <h2 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
          <Package className="w-4 h-4 text-amber-500" />
          {t('driverPortal.activeDeliveries')} ({deliveries.active?.length || 0})
        </h2>
        {(!deliveries.active || deliveries.active.length === 0) ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
            <Package className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
            <p className="text-sm text-zinc-500">{t('driverPortal.noDeliveriesNow')}</p>
            <p className="text-xs text-zinc-600 mt-1">{t('driverPortal.newOrdersAppear')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deliveries.active.map(d => {
              const next = getNextStatus(d.status);
              const nextLabel = getNextStatusLabel(d.status);
              return (
                <div key={d.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`driver-delivery-${d.id}`}>
                  {/* Status bar */}
                  <div className="flex">
                    {STATUS_FLOW.map((s, i) => (
                      <div key={s} className={`flex-1 h-1.5 ${STATUS_FLOW.indexOf(d.status) >= i ? getStatusColor(s) : 'bg-zinc-800'}`} />
                    ))}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-sm">{d.customer_name}</p>
                        <p className="text-xs text-zinc-500">#{d.id?.slice(0, 6)} - {d.total_price} ر.س</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(d.status)} text-white`}>
                        {getStatusLabel(d.status)}
                      </span>
                    </div>

                    {/* Customer info + contact */}
                    <div className="space-y-2 mb-4">
                      {d.customer_address && (
                        <button onClick={() => openMaps(d.customer_address)}
                          className="flex items-center gap-2 text-xs text-zinc-400 hover:text-blue-400 transition-colors w-full text-start"
                          data-testid={`maps-btn-${d.id}`}>
                          <Navigation className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{d.customer_address}</span>
                          <span className="text-[10px] text-blue-400 mr-auto flex-shrink-0">{t('driverPortal.maps')}</span>
                        </button>
                      )}
                      <div className="flex items-center gap-2 text-xs text-zinc-400">
                        <Package className="w-3.5 h-3.5" /> {d.items_count} {t('driverPortal.items')}
                      </div>
                    </div>

                    {/* WhatsApp Quick Actions */}
                    {d.customer_phone && (
                      <div className="flex gap-2 mb-3" data-testid={`contact-actions-${d.id}`}>
                        {/* WhatsApp button - changes message based on status */}
                        <button onClick={() => {
                          const msg = d.status === 'assigned'
                            ? getWAMessage('assigned', d.customer_name)
                            : d.status === 'picked_up'
                            ? getWAMessage('picked_up', d.customer_name)
                            : d.status === 'on_the_way'
                            ? getWAMessage('on_the_way', d.customer_name)
                            : getWAMessage('arrived', d.customer_name);
                          openWhatsApp(d.customer_phone, msg);
                        }}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600/20 text-emerald-400 text-xs font-semibold hover:bg-emerald-600/30 transition-colors"
                          data-testid={`wa-btn-${d.id}`}>
                          <MessageSquare className="w-4 h-4" />
                          {d.status === 'assigned' ? t('driverPortal.notifyCustomer') : d.status === 'on_the_way' ? t('driverPortal.imClose') : t('driverPortal.whatsapp')}
                        </button>
                        {/* Call button */}
                        <a href={`tel:${d.customer_phone}`}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs font-semibold hover:bg-zinc-700 transition-colors"
                          data-testid={`call-btn-${d.id}`}>
                          <Phone className="w-4 h-4" />
                          {t('driverPortal.call')}
                        </a>
                      </div>
                    )}

                    {/* "Arrived" WhatsApp shortcut when on_the_way */}
                    {d.status === 'on_the_way' && d.customer_phone && (
                      <button onClick={() => openWhatsApp(d.customer_phone, getWAMessage('arrived', d.customer_name))}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-600/15 text-blue-400 text-xs font-medium hover:bg-blue-600/25 transition-colors mb-3"
                        data-testid={`arrived-wa-${d.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" />
                        {t('driverPortal.arrivedNotify')}
                      </button>
                    )}

                    {/* Status action button */}
                    {next && (
                      <Button onClick={() => updateDeliveryStatus(d.id, next)}
                        className={`w-full h-12 text-white font-bold gap-2 ${next === 'delivered' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                        data-testid={`delivery-action-${d.id}`}>
                        {next === 'delivered' ? <CheckCircle2 className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                        {nextLabel}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Completed Today - phone hidden for privacy */}
      {deliveries.completed?.length > 0 && (
        <div className="px-3 mt-6 pb-6">
          <h2 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            {t('driverPortal.completedTitle')} ({deliveries.completed.length})
          </h2>
          <div className="flex items-center gap-1.5 mb-3 text-[10px] text-zinc-600">
            <Shield className="w-3 h-3" />
            <span>{t('driverPortal.privacyNotice')}</span>
          </div>
          <div className="space-y-2">
            {deliveries.completed.slice(0, 5).map(d => (
              <div key={d.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-3 flex items-center gap-3" data-testid={`completed-delivery-${d.id}`}>
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{d.customer_name?.charAt(0)}{'*'.repeat(Math.max(0, (d.customer_name?.length || 1) - 1))}</p>
                  <p className="text-xs text-zinc-500">{d.total_price} ر.س - #{d.id?.slice(0, 6)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
